import React, { useEffect, useState } from 'react';
import { Table, Select, DatePicker, message, Tooltip } from 'antd';
import { FileText } from 'lucide-react';
import dayjs from 'dayjs';
import { AdminCard, AdminCardToolbar, AdminPageHeader, AdminStatusPill } from '../../ui';
import { getAuditLogs, getAuditActions, AuditLog } from '../../../../api/admin/audit.api';

const { RangePicker } = DatePicker;

// Nhãn + màu cho từng loại hành động (mã do backend phát).
const ACTION_META: Record<string, { label: string; color: string }> = {
  CONFIG_UPDATE: { label: 'Đổi cấu hình', color: 'blue' },
  POST_APPROVE: { label: 'Duyệt bài', color: 'green' },
  POST_DELETE: { label: 'Xoá bài', color: 'red' },
  POST_HIDE: { label: 'Ẩn bài', color: 'orange' },
  REPORT_HANDLE: { label: 'Xử lý report', color: 'volcano' },
  ROLE_CHANGE: { label: 'Đổi quyền admin', color: 'purple' },
  AUTH_RESET: { label: 'Reset Authenticator', color: 'magenta' },
  POST_REJECT: { label: 'Từ chối bài', color: 'red' },
  USER_BLOCK: { label: 'Khoá / mở tài khoản', color: 'red' },
  USER_DELETE: { label: 'Xoá tài khoản', color: 'red' },
  USER_DONATE_TOGGLE: { label: 'Bật / tắt nhận donate', color: 'gold' },
  CREATOR_APPROVE: { label: 'Duyệt Creator', color: 'green' },
  DONATE_REQUEST_APPROVE: { label: 'Duyệt mở donate', color: 'green' },
  DONATE_REQUEST_REJECT: { label: 'Từ chối mở donate', color: 'red' },
  COMMENT_BLOCK: { label: 'Khoá bình luận', color: 'orange' },
  COMMENT_UNLOCK: { label: 'Mở bình luận', color: 'cyan' },
  WITHDRAW_APPROVE: { label: 'Duyệt rút tiền', color: 'green' },
  WITHDRAW_REJECT: { label: 'Từ chối rút tiền', color: 'red' },
  DONATE_EVENT_CREATE: { label: 'Tạo sự kiện donate', color: 'green' },
  DONATE_EVENT_UPDATE: { label: 'Sửa sự kiện donate', color: 'blue' },
  DONATE_EVENT_CANCEL: { label: 'Huỷ sự kiện donate', color: 'red' },
  DONATE_EVENT_RETRY: { label: 'Trao lại quà sự kiện', color: 'gold' },
  ARENA_CREATE: { label: 'Tạo thử thách Arena', color: 'green' },
  ARENA_UPDATE: { label: 'Sửa thử thách Arena', color: 'blue' },
  ARENA_PUBLISH: { label: 'Mở thử thách Arena', color: 'cyan' },
  ARENA_CANCEL: { label: 'Huỷ thử thách Arena', color: 'red' },
  ARENA_SCORE: { label: 'Chấm điểm Arena', color: 'gold' },
  ARENA_DISQUALIFY: { label: 'Loại đội Arena', color: 'red' },
  ARENA_FINALIZE: { label: 'Chốt kết quả Arena', color: 'purple' },
  // Rà soát 28/10: các mã đã phát ở máy chủ nhưng chưa có nhãn (trước đây hiện mã tiếng Anh thô).
  CREATOR_AD_EARNING_VOID: { label: 'Huỷ thu nhập quảng cáo creator', color: 'red' },
  ADS_TOPUP_REVIEW: { label: 'Đối soát nạp ví doanh nghiệp', color: 'gold' },
  ADS_CREDIT_LIMIT_SET: { label: 'Đổi hạn mức trả sau', color: 'gold' },
  ADS_BUSINESS_ACCOUNT_REVIEW: { label: 'Duyệt hồ sơ doanh nghiệp', color: 'green' },
  ADS_CAMPAIGN_REVIEW: { label: 'Duyệt chiến dịch quảng cáo', color: 'green' },
  PUBLISHER_PAYOUT: { label: 'Chi trả publisher', color: 'gold' },
  AFFILIATE_COMMISSION_RECORD: { label: 'Ghi tay hoa hồng affiliate', color: 'gold' },
  AFFILIATE_COMMISSION_SETTLE_NOW: { label: 'Chốt sớm hoa hồng affiliate', color: 'gold' },
  GAMIFICATION_CONFIG_UPDATE: { label: 'Đổi cấu hình Ca đào', color: 'blue' },
  REPORT_COMMENT_APPROVE: { label: 'Duyệt báo cáo bình luận', color: 'volcano' },
  REPORT_COMMENT_REJECT: { label: 'Bác báo cáo bình luận', color: 'default' },
  BRAND_CAMPAIGN_APPROVE: { label: 'Sàn hợp tác: duyệt chiến dịch', color: 'green' },
  BRAND_CAMPAIGN_REJECT: { label: 'Sàn hợp tác: từ chối chiến dịch', color: 'red' },
  BRAND_DEAL_RESOLVE_PAY: { label: 'Sàn hợp tác: trả creator', color: 'gold' },
  BRAND_DEAL_RESOLVE_REFUND: { label: 'Sàn hợp tác: hoàn doanh nghiệp', color: 'gold' },
  BRAND_DISPUTE_UPHOLD: { label: 'Sàn hợp tác: chấp nhận kháng cáo', color: 'orange' },
  BRAND_DISPUTE_DISMISS: { label: 'Sàn hợp tác: bác kháng cáo', color: 'default' },
  BRAND_TASK_CANCEL_APPROVE: { label: 'Sàn hợp tác: duyệt huỷ nhiệm vụ', color: 'orange' },
  BRAND_TASK_CANCEL_REJECT: { label: 'Sàn hợp tác: từ chối huỷ nhiệm vụ', color: 'default' },
  BRAND_LIVE_CANCEL_APPROVE: { label: 'Quảng cáo live: duyệt huỷ', color: 'orange' },
  BRAND_LIVE_CANCEL_REJECT: { label: 'Quảng cáo live: từ chối huỷ', color: 'default' },
  BRAND_LIVE_REPORT_UPHELD: { label: 'Quảng cáo live: xác nhận gian lận', color: 'red' },
  BRAND_LIVE_REPORT_DISMISSED: { label: 'Quảng cáo live: bác báo gian lận', color: 'default' },
  BRAND_SANCTION_APPLY: { label: 'Xử phạt người đặt đơn', color: 'red' },
  BRAND_SANCTION_REVOKE: { label: 'Gỡ xử phạt', color: 'cyan' },
  BRAND_BLACKLIST_REMOVE: { label: 'Gỡ khỏi danh sách đen', color: 'cyan' },
  BRAND_MARKETPLACE_BAN: { label: 'Cấm khỏi sàn hợp tác', color: 'red' },
  BRAND_MARKETPLACE_UNBAN: { label: 'Bỏ cấm sàn hợp tác', color: 'cyan' },
  BRAND_RATING_HIDE: { label: 'Ẩn đánh giá', color: 'orange' },
  BRAND_RATING_UNHIDE: { label: 'Hiện lại đánh giá', color: 'cyan' },
  BRAND_REPUTATION_EXEMPT: { label: 'Miễn trừ uy tín', color: 'purple' },
  BRAND_REPUTATION_UNEXEMPT: { label: 'Bỏ miễn trừ uy tín', color: 'purple' },
  BRAND_CONVERSION_VOID: { label: 'Huỷ lượt chuyển đổi', color: 'red' },
  BRAND_LEDGER_EXPORT: { label: 'Xuất sổ dòng tiền', color: 'blue' },
  BRAND_INCOME_EXPORT: { label: 'Xuất thu nhập creator', color: 'blue' },
};
// Màu antd của từng mã => họ màu admin (không có tím/hồng/cam).
const COLOR_TONE: Record<string, 'brand' | 'success' | 'danger' | 'warning' | 'neutral'> = {
  blue: 'brand',
  cyan: 'brand',
  purple: 'brand',
  magenta: 'brand',
  green: 'success',
  red: 'danger',
  volcano: 'danger',
  orange: 'warning',
  gold: 'warning',
  default: 'neutral',
};
const actionTag = (a: string) => {
  const m = ACTION_META[a];
  if (!m) return <AdminStatusPill tone="neutral" className="font-mono">{a}</AdminStatusPill>;
  return <AdminStatusPill tone={COLOR_TONE[m.color] ?? 'neutral'}>{m.label}</AdminStatusPill>;
};

const ManagementAuditLog = () => {
  const [data, setData] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<string | undefined>();
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [actions, setActions] = useState<string[]>([]);

  useEffect(() => {
    getAuditActions().then(setActions).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAuditLogs({
        pageNumber: page,
        pageSize,
        action,
        from: range?.[0]?.startOf('day').toISOString(),
        to: range?.[1]?.endOf('day').toISOString(),
      });
      setData(res.items);
      setTotal(res.total);
    } catch {
      message.error('Không tải được nhật ký.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, action, range]);

  const columns = [
    {
      title: 'Thời gian',
      dataIndex: 'createdAt',
      width: 160,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      width: 140,
      render: actionTag,
    },
    {
      title: 'Người thực hiện',
      dataIndex: 'actorName',
      width: 180,
      render: (v: string, r: AuditLog) => (
        <Tooltip title={r.actorId}>
          <span>{v || r.actorId?.slice(0, 8) || '—'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Đối tượng',
      dataIndex: 'targetType',
      width: 130,
      render: (v: string, r: AuditLog) => (
        <span className="adm-muted">
          {v}
          {r.targetId ? ` #${r.targetId}` : ''}
        </span>
      ),
    },
    {
      title: 'Chi tiết',
      dataIndex: 'detail',
      render: (v: string) => <span className="break-words">{v}</span>,
    },
  ];

  return (
    <div className="adm-page">
      <AdminPageHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <span className="adm-stat__icon adm-stat__icon--brand" aria-hidden="true">
              <FileText size={20} />
            </span>
            Nhật ký hành động admin
          </span>
        }
        description="Lịch sử duyệt/xoá bài, xử lý report, đổi quyền admin và đổi cấu hình: ai làm, khi nào, chi tiết."
      />

      <AdminCard flush>
        <AdminCardToolbar style={{ flexWrap: 'wrap', gap: 12, borderBottom: 0, justifyContent: 'flex-start' }}>
          <Select
            allowClear
            placeholder="Lọc theo hành động"
            className="min-w-[220px]"
            value={action}
            onChange={(v) => {
              setPage(1);
              setAction(v);
            }}
            options={actions.map((a) => ({
              value: a,
              label: ACTION_META[a]?.label || a,
            }))}
          />
          <RangePicker
            format="DD/MM/YYYY"
            value={range as any}
            placeholder={['Ngày bắt đầu', 'Ngày kết thúc']}
            onChange={(v) => {
              setPage(1);
              setRange(v as any);
            }}
          />
        </AdminCardToolbar>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns as any}
          dataSource={data}
          scroll={{ x: 900 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </AdminCard>
    </div>
  );
};

export default ManagementAuditLog;
